// Ory authentication service
import {
  Configuration,
  FrontendApi,
  Identity,
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  SettingsFlow,
  VerificationFlow,
  SuccessfulNativeLogin,
  SuccessfulNativeRegistration,
  UpdateLoginFlowBody,
  UpdateRecoveryFlowBody,
  UpdateRegistrationFlowBody,
  UpdateSettingsFlowBody,
  UpdateVerificationFlowBody,
  UiNodeInputAttributes,
} from '@ory/client';
import { OryConfig, OrySessionInfo, OryIdentityTraits, ApiError } from '@types';
import { User, UserRole } from '@types';
import authService from '../auth/auth.service';
import { getBorrowerByEmail } from '../borrower.service';
import { isBackendUserNotFoundError } from '../../utils/ory-provisioning.utils';
import { getIdentityNameParts } from '../../utils/ory-identity.utils';
import { getOryVerificationContinuationUrl } from '../../utils/ory-flow.utils';

class OryService {
  private client: FrontendApi;
  private config: OryConfig;
  private readonly backendProvisioningAttempts = 6;
  private readonly backendProvisioningRetryDelayMs = 1000;

  constructor() {
    // Initialize configuration from environment variables
    this.config = {
      sdkUrl: import.meta.env.VITE_ORY_SDK_URL || '',
      uiUrl: import.meta.env.VITE_ORY_UI_URL || '',
    };

    // Initialize Ory client
    const configuration = new Configuration({
      basePath: this.config.sdkUrl,
      baseOptions: {
        withCredentials: true, // Important for cookie-based sessions
      },
    });

    this.client = new FrontendApi(configuration);
  }

  /**
   * Open the application's custom Ory browser-flow login UI.
   */
  async createLoginFlow(returnTo?: string): Promise<void> {
    const loginUrl = new URL('/login', window.location.origin);
    if (returnTo) loginUrl.searchParams.set('return_to', returnTo);
    window.location.href = loginUrl.toString();
  }

  async initializeLoginFlow(returnTo?: string, aal?: string, via?: string): Promise<LoginFlow> {
    const { data } = await this.client.createBrowserLoginFlow({ returnTo, aal, via });
    return data;
  }

  async getLoginFlow(flowId: string): Promise<LoginFlow> {
    const { data } = await this.client.getLoginFlow({ id: flowId });
    return data;
  }

  async submitLoginFlow(flowId: string, body: UpdateLoginFlowBody): Promise<SuccessfulNativeLogin> {
    const { data } = await this.client.updateLoginFlow({
      flow: flowId,
      updateLoginFlowBody: body,
    });
    return data;
  }

  /**
   * Start registration in the application's custom Ory browser-flow UI.
   */
  async createRegistrationFlow(returnTo?: string): Promise<void> {
    const defaultReturnUrl = new URL('/auth/callback', window.location.origin);
    defaultReturnUrl.searchParams.set('registration_verification', 'complete');
    const returnUrl = returnTo || defaultReturnUrl.toString();
    const registrationUrl = new URL('/registration', window.location.origin);
    registrationUrl.searchParams.set('return_to', returnUrl);

    window.location.href = registrationUrl.toString();
  }

  /** Create the Ory browser flow rendered by the custom registration page. */
  async initializeRegistrationFlow(
    returnTo?: string,
    identitySchema?: string
  ): Promise<RegistrationFlow> {
    const { data } = await this.client.createBrowserRegistrationFlow({ returnTo, identitySchema });
    return data;
  }

  /** Retrieve an existing browser flow without replacing its ID or CSRF token. */
  async getRegistrationFlow(flowId: string): Promise<RegistrationFlow> {
    const { data } = await this.client.getRegistrationFlow({ id: flowId });
    return data;
  }

  /** Submit a browser registration without navigating away from our registration UI. */
  async submitRegistrationFlow(
    flowId: string,
    body: UpdateRegistrationFlowBody
  ): Promise<SuccessfulNativeRegistration> {
    const { data } = await this.client.updateRegistrationFlow({
      flow: flowId,
      updateRegistrationFlowBody: body,
    });
    return data;
  }

  /** Start a code verification for a specific verifiable address (email or SMS). */
  async initializeVerificationForAddress(address: string, returnTo?: string): Promise<string> {
    const { data: flow } = await this.client.createBrowserVerificationFlow({ returnTo });
    const csrfNode = flow.ui.nodes.find(
      (node) =>
        node.type === 'input' && (node.attributes as UiNodeInputAttributes).name === 'csrf_token'
    );
    const csrfToken =
      csrfNode?.type === 'input'
        ? String((csrfNode.attributes as UiNodeInputAttributes).value ?? '')
        : undefined;

    // Ory's verification API calls this field `email` for compatibility, but
    // it accepts any schema-configured verifiable address. A telephone trait
    // configured with `verification.via: sms` is delivered through SMS.
    await this.client.updateVerificationFlow({
      flow: flow.id,
      updateVerificationFlowBody: {
        method: 'code',
        email: address,
        csrf_token: csrfToken,
      },
    });

    return flow.id;
  }

  verificationUiUrl(
    flowId: string,
    codeOnly = false,
    verificationMethod?: 'email' | 'sms'
  ): string {
    return this.applicationFlowUrl('/verification', {
      flow: flowId,
      ...(codeOnly ? { code_only: 'true' } : {}),
      ...(verificationMethod ? { verification_method: verificationMethod } : {}),
    });
  }

  private applicationFlowUrl(path: string, parameters: Record<string, string>): string {
    const url = new URL(path, window.location.origin);
    Object.entries(parameters).forEach(([name, value]) => url.searchParams.set(name, value));
    return url.toString();
  }

  async getVerificationFlow(flowId: string): Promise<VerificationFlow> {
    const { data } = await this.client.getVerificationFlow({ id: flowId });
    return data;
  }

  async submitVerificationFlow(
    flowId: string,
    body: UpdateVerificationFlowBody
  ): Promise<VerificationFlow> {
    const { data } = await this.client.updateVerificationFlow({
      flow: flowId,
      updateVerificationFlowBody: body,
    });
    return data;
  }

  private getSettingsFlowCsrfToken(
    nodes: Array<{ type?: string; attributes?: unknown }>
  ): string | undefined {
    const csrfNode = nodes.find(
      (node) =>
        node.type === 'input' && (node.attributes as UiNodeInputAttributes).name === 'csrf_token'
    );

    return csrfNode?.type === 'input'
      ? String((csrfNode.attributes as UiNodeInputAttributes).value ?? '')
      : undefined;
  }

  private getSettingsFlowTraitNames(
    nodes: Array<{ type?: string; attributes?: unknown }>
  ): Set<string> {
    return nodes.reduce((traitNames, node) => {
      if (node.type !== 'input') return traitNames;

      const attributes = node.attributes as UiNodeInputAttributes;
      if (typeof attributes.name === 'string' && attributes.name.startsWith('traits.')) {
        traitNames.add(attributes.name.replace(/^traits\./, ''));
      }

      return traitNames;
    }, new Set<string>());
  }

  private filterAllowedSettingsTraits(
    traits: Record<string, unknown>,
    allowedTraitNames: Set<string>
  ): Record<string, unknown> {
    if (allowedTraitNames.size === 0) return traits;

    return Object.fromEntries(
      Object.entries(traits).filter(([traitName]) => allowedTraitNames.has(traitName))
    );
  }

  /** Update Ory profile traits while preserving existing identity traits by default. */
  async updateProfileTraits(
    traits: Record<string, unknown>,
    traitsToRemove: string[] = [],
    returnTo?: string
  ): Promise<Identity> {
    const result = await this.updateProfileTraitsWithVerification(traits, traitsToRemove, returnTo);
    return result.identity;
  }

  /** Update profile traits and expose any verification flow Ory created for a changed address. */
  async updateProfileTraitsWithVerification(
    traits: Record<string, unknown>,
    traitsToRemove: string[] = [],
    returnTo?: string
  ): Promise<{ identity: Identity; verificationUrl?: string }> {
    const { data: flow } = await this.client.createBrowserSettingsFlow({ returnTo });
    const csrfToken = this.getSettingsFlowCsrfToken(flow.ui.nodes);
    const allowedTraitNames = this.getSettingsFlowTraitNames(flow.ui.nodes);
    const existingTraits = this.filterAllowedSettingsTraits(
      flow.identity.traits as Record<string, unknown>,
      allowedTraitNames
    );
    const requestedTraits = this.filterAllowedSettingsTraits(traits, allowedTraitNames);
    const retainedTraits = Object.fromEntries(
      Object.entries(existingTraits).filter(([traitName]) => !traitsToRemove.includes(traitName))
    );

    const { data: updatedFlow } = await this.client.updateSettingsFlow({
      flow: flow.id,
      updateSettingsFlowBody: {
        method: 'profile',
        csrf_token: csrfToken,
        traits: { ...retainedTraits, ...requestedTraits },
      },
    });

    const requestedAddress = Object.values(requestedTraits).find(
      (value): value is string => typeof value === 'string' && value.startsWith('+')
    );
    let verificationUrl = requestedAddress
      ? getOryVerificationContinuationUrl(updatedFlow.continue_with, requestedAddress)
      : undefined;
    const requestedAddressValues = new Set(
      Object.values(requestedTraits).filter((value): value is string => typeof value === 'string')
    );
    const hasPendingRequestedAddress = updatedFlow.identity.verifiable_addresses?.some(
      (address) => !address.verified && requestedAddressValues.has(address.value)
    );

    // Ory can persist the continuation just after dispatching the SMS. Briefly read this same
    // settings flow for up to one second; never repeat the profile update or initialize an
    // address-specific flow.
    if (hasPendingRequestedAddress && requestedAddress && !verificationUrl) {
      verificationUrl = await this.getSettingsVerificationContinuationUrl(
        updatedFlow,
        requestedAddress
      );
    }

    return { identity: updatedFlow.identity, verificationUrl };
  }

  /**
   * Open the application's custom Ory browser-flow recovery UI.
   */
  async createRecoveryFlow(returnTo?: string): Promise<void> {
    const recoveryUrl = new URL('/recovery', window.location.origin);
    if (returnTo) recoveryUrl.searchParams.set('return_to', returnTo);
    window.location.href = recoveryUrl.toString();
  }

  async initializeRecoveryFlow(returnTo?: string): Promise<RecoveryFlow> {
    const { data } = await this.client.createBrowserRecoveryFlow({ returnTo });
    return data;
  }

  async getRecoveryFlow(flowId: string): Promise<RecoveryFlow> {
    const { data } = await this.client.getRecoveryFlow({ id: flowId });
    return data;
  }

  async submitRecoveryFlow(flowId: string, body: UpdateRecoveryFlowBody): Promise<RecoveryFlow> {
    const { data } = await this.client.updateRecoveryFlow({
      flow: flowId,
      updateRecoveryFlowBody: body,
    });
    return data;
  }

  /**
   * Create a settings flow (account settings) and redirect to Ory UI
   * Uses direct browser redirect (no AJAX) to avoid CORS issues
   */
  async createSettingsFlow(returnTo?: string): Promise<void> {
    const returnUrl = returnTo || `${window.location.origin}/account/settings`;
    const settingsUrl = `${this.config.sdkUrl}/self-service/settings/browser?return_to=${encodeURIComponent(returnUrl)}`;

    // Direct browser redirect - no AJAX, no CORS issues
    window.location.href = settingsUrl;
  }

  /** Create a settings browser flow for the application-owned settings form. */
  async initializeSettingsFlow(returnTo?: string): Promise<SettingsFlow> {
    const { data } = await this.client.createBrowserSettingsFlow({ returnTo });
    return data;
  }

  async submitSettingsFlow(flowId: string, body: UpdateSettingsFlowBody): Promise<SettingsFlow> {
    const { data } = await this.client.updateSettingsFlow({
      flow: flowId,
      updateSettingsFlowBody: body,
    });
    return data;
  }

  /**
   * Ory may expose the address continuation just after persisting a settings update. Read the
   * already-submitted flow for a bounded interval; this deliberately never updates it again.
   */
  async getSettingsVerificationContinuationUrl(
    updatedFlow: SettingsFlow,
    address: string
  ): Promise<string | undefined> {
    let verificationUrl = getOryVerificationContinuationUrl(updatedFlow.continue_with, address);
    for (let attempt = 0; !verificationUrl && attempt < 4; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 250));
      try {
        const { data: refreshedFlow } = await this.client.getSettingsFlow({ id: updatedFlow.id });
        verificationUrl = getOryVerificationContinuationUrl(refreshedFlow.continue_with, address);
      } catch {
        // The update succeeded; a transient read failure must never cause it to be resubmitted.
      }
    }
    return verificationUrl;
  }

  /**
   * Logout and redirect to Ory logout
   * Two-step process: fetch logout URL, then redirect to it
   */
  async logout(): Promise<void> {
    console.log('Ory logout initiated');

    try {
      // Step 1: Fetch the logout token/URL from the browser endpoint
      // Do NOT include return_to parameter as it may not be whitelisted
      const logoutBrowserUrl = `${this.config.sdkUrl}/self-service/logout/browser`;
      console.log('Fetching logout URL from:', logoutBrowserUrl);

      const response = await fetch(logoutBrowserUrl, {
        method: 'GET',
        credentials: 'include', // Important: sends session cookies
      });

      console.log('Logout browser response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to get logout URL: ${response.status}`);
      }

      const data = await response.json();
      console.log('Logout data received:', data);

      // Step 2: Redirect to the logout URL with token
      if (data.logout_url) {
        console.log('Redirecting to logout URL:', data.logout_url);
        window.location.href = data.logout_url;
        return;
      }

      throw new Error('No logout_url in response');
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: redirect to login page and let auth store clear state
      console.warn('Forcing redirect to login page');
      window.location.href = `${window.location.origin}/login`;
    }
  }

  /**
   * Get current session from Ory
   * Returns null if no active session
   */
  async getSession(): Promise<OrySessionInfo | null> {
    try {
      const { data: session } = await this.client.toSession();

      if (session && session.active && session.identity) {
        return {
          session,
          identity: session.identity,
          isActive: session.active,
          expiresAt: session.expires_at,
        };
      }

      return null;
    } catch (error) {
      // 401 or 403 means no active session
      console.debug('No active Ory session:', error);
      return null;
    }
  }

  /**
   * Check if there is an active Ory session
   */
  async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession();
    return session !== null && session.isActive;
  }

  /**
   * Get current user identity from Ory (whoami)
   */
  async whoami(): Promise<Identity | null> {
    try {
      const sessionInfo = await this.getSession();
      return sessionInfo?.identity || null;
    } catch (error) {
      console.error('Failed to get user identity:', error);
      return null;
    }
  }

  /**
   * Transform Ory identity to application User type
   */
  toAppUser(identity: Identity): User {
    const traits = identity.traits as OryIdentityTraits;
    const { firstName, lastName } = getIdentityNameParts(traits.name);
    const identityRole = this.getIdentityRole(identity, traits);
    const mappedTraitRole = identityRole
      ? this.mapBackendRoleToUserRole(identityRole)
      : UserRole.USER;

    return {
      id: identity.id,
      email: traits.email || '',
      firstName,
      lastName,
      role: mappedTraitRole,
      createdAt: identity.created_at || new Date().toISOString(),
      updatedAt: identity.updated_at || new Date().toISOString(),
    };
  }

  private getIdentityRole(identity: Identity, traits: OryIdentityTraits): string | undefined {
    const traitRecord = traits as Record<string, unknown>;

    if (typeof traits.role === 'string' && traits.role.trim()) {
      return traits.role;
    }

    if (Array.isArray(traitRecord.roles)) {
      const firstRole = traitRecord.roles.find(
        (role): role is string => typeof role === 'string' && role.trim().length > 0
      );
      if (firstRole) {
        return firstRole;
      }
    }

    const directTraitRoleKeys = ['appRole', 'default_role', 'x-role', 'user_role'];
    for (const key of directTraitRoleKeys) {
      const value = traitRecord[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    const hasuraClaims = traitRecord['https://hasura.io/jwt/claims'];
    if (typeof hasuraClaims === 'object' && hasuraClaims !== null) {
      const claimsRecord = hasuraClaims as Record<string, unknown>;
      const defaultRole = claimsRecord['x-hasura-default-role'];
      if (typeof defaultRole === 'string' && defaultRole.trim()) {
        return defaultRole;
      }
    }

    const metadataPublicRole = (identity as Identity & { metadata_public?: { role?: unknown } })
      .metadata_public?.role;
    if (typeof metadataPublicRole === 'string' && metadataPublicRole.trim()) {
      return metadataPublicRole;
    }

    const metadataAdminRole = (identity as Identity & { metadata_admin?: { role?: unknown } })
      .metadata_admin?.role;
    if (typeof metadataAdminRole === 'string' && metadataAdminRole.trim()) {
      return metadataAdminRole;
    }

    return undefined;
  }

  /**
   * Get current user in application format
   * Fetches user data from backend to get borrowerId and other details
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const identity = await this.whoami();
      if (!identity) {
        return null;
      }

      // Get basic user info from Ory identity
      const oryUser = this.toAppUser(identity);

      const authMeUnavailable = sessionStorage.getItem('auth_me_unavailable') === 'true';

      // Preferred: fetch current user from /auth/me (self endpoint).
      // If this endpoint is missing or forbidden in an environment, fall back
      // to users-table lookup by email and then Ory identity data.
      let authMeStatus: number | undefined;
      if (!authMeUnavailable) {
        try {
          const meUser = await this.getBackendUserAfterProvisioning();
          return {
            ...oryUser,
            ...meUser,
            borrowerId: meUser.borrowerId || meUser.id,
            role: this.mapBackendRoleToUserRole(meUser.role),
          };
        } catch (meError) {
          if (isBackendUserNotFoundError(meError)) {
            throw meError;
          }

          authMeStatus = this.getStatusCode(meError);
          if (authMeStatus === 404) {
            sessionStorage.setItem('auth_me_unavailable', 'true');
          }

          if (
            authMeStatus &&
            authMeStatus !== 401 &&
            authMeStatus !== 403 &&
            authMeStatus !== 404
          ) {
            console.warn(
              'Failed to fetch /auth/me user profile, trying users/by-email fallback:',
              meError
            );
          }
        }
      }

      const usersByEmailUnavailable =
        sessionStorage.getItem('users_by_email_unavailable') === 'true';

      // Fallback to users-table lookup by email for role + borrowerId when
      // /auth/me is unavailable and no legacy token is present.
      const shouldTryUsersByEmail =
        !usersByEmailUnavailable && authMeStatus !== 401 && authMeStatus !== 403;

      if (shouldTryUsersByEmail) {
        try {
          const backendResponse = await getBorrowerByEmail(oryUser.email);
          if (backendResponse.success && backendResponse.data) {
            const backendUser = backendResponse.data;
            return {
              ...oryUser,
              id: backendUser.id,
              borrowerId: backendUser.id,
              firstName: backendUser.fullName.split(' ')[0] || oryUser.firstName,
              lastName: backendUser.fullName.split(' ').slice(1).join(' ') || oryUser.lastName,
              role: this.mapBackendRoleToUserRole(backendUser.role),
            };
          }
        } catch (backendError) {
          const statusCode = this.getStatusCode(backendError);
          if (statusCode === 401 || statusCode === 403 || statusCode === 404) {
            sessionStorage.setItem('users_by_email_unavailable', 'true');
          } else if (statusCode) {
            console.warn('Failed users/by-email lookup, using Ory identity only:', backendError);
          }
        }
      }

      return {
        ...oryUser,
        borrowerId: oryUser.id,
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  private async getBackendUserAfterProvisioning(): Promise<User> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= this.backendProvisioningAttempts; attempt += 1) {
      try {
        return await authService.getCurrentUser();
      } catch (error) {
        lastError = error;
        if (!isBackendUserNotFoundError(error) || attempt === this.backendProvisioningAttempts) {
          throw error;
        }

        // Ory registration and the backend provisioning webhook complete independently. Give the
        // webhook a short window to create the application user before loading protected data.
        await new Promise((resolve) => setTimeout(resolve, this.backendProvisioningRetryDelayMs));
      }
    }

    throw lastError;
  }

  private getStatusCode(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) {
      return undefined;
    }

    const apiError = error as Partial<ApiError>;
    if (typeof apiError.statusCode === 'number') {
      return apiError.statusCode;
    }

    return undefined;
  }

  /**
   * Map backend role string to UserRole enum
   */
  private mapBackendRoleToUserRole(backendRole: string): UserRole {
    const roleUpper = backendRole.trim().toUpperCase();
    console.log('[mapBackendRoleToUserRole] raw=', backendRole, 'normalized=', roleUpper);
    if (roleUpper === 'ADMIN') return UserRole.ADMIN;
    if (roleUpper === 'UNDERWRITER') return UserRole.UNDERWRITER;
    if (roleUpper === 'SALESPERSON') return UserRole.SALESPERSON;
    if (roleUpper === 'BORROWER' || roleUpper === 'USER') return UserRole.USER;
    return UserRole.GUEST;
  }

  /**
   * Get Ory configuration
   */
  getConfig(): OryConfig {
    return this.config;
  }

  /**
   * Check if Ory is configured
   */
  isConfigured(): boolean {
    return !!this.config.sdkUrl && !!this.config.uiUrl;
  }
}

export default new OryService();
